import { Component, Mixins, Watch } from "vue-property-decorator";
import { Getter, Mutation } from "vuex-class";
import { AUTH_MODULE_NAME } from "@/store/modules/auth";
import TimerUtils from "@/modules/Timer/TimerUtils";
import auth from '@/models/modules/auth';
import NotificationCore from "@/modules/NotificationCore/NotificationCore";
import { authEntity } from "@/models/modules/auth/entity";
import CustomerSessionExpired from '@/feature/Auth/components/CustomerSessionExpired.vue';
import CustomerActivitySettings from "./CustomerActivitySettings";
import OptimizedActionCaller from "@/mixins/debounce/OptimizedActionCaller";

@Component({
	components: {
		CustomerSessionExpired
	}
})
export default class CustomerActivityController extends Mixins(
	CustomerActivitySettings,
	OptimizedActionCaller
) {

	@Getter('isAuth', { namespace: AUTH_MODULE_NAME })
	readonly isAuth!: boolean

	@Getter('customerSessionExpired', { namespace: AUTH_MODULE_NAME })
	readonly customerSessionExpired!: boolean

	@Getter('resetNotificationTrack', { namespace: AUTH_MODULE_NAME })
	readonly resetNotificationTrack!: boolean

	@Mutation('setCustomerSessionExpired', { namespace: AUTH_MODULE_NAME })
	setCustomerSessionExpired!: (_value: boolean) => void

	@Mutation('setResetNotificationTrack', { namespace: AUTH_MODULE_NAME })
	setResetNotificationTrack!: (_value: boolean) => void

	customerActivityTrackerId: number | null = null

	customerActivityNotifyTrackerId: number | null = null

	customerSessionExpiredDialog: boolean = false

	customerActivityNotificationId: number | null = null

	customerActivationEventListeners: ((handler: () => void) => void)[] = [
		handler => document.body.addEventListener('click', handler),
		handler => document.addEventListener('scroll', handler),
		handler => document.addEventListener('keyup', handler),
		handler => document.addEventListener('mousemove', handler)
	]

	created() {
		this.initCustomerActivity();
	}

	activateCustomer() {
		this.customerSessionExpired
			|| this.checkIsAuthAndTrackCustomerActivity();
	}

	checkIsAuthAndTrackCustomerActivity() {
		this.isAuth &&
			this.trackCustomerActivity();
	}

	trackCustomerActivityNotification() {
		this.customerActivityNotifyTrackerId &&
			clearTimeout(this.customerActivityNotifyTrackerId);

		this.customerActivityNotificationId &&
			NotificationCore.deleteToastById(this.customerActivityNotificationId);

		this.customerActivityNotifyTrackerId = setTimeout(
			() => this.notifyCustomerSessionExpiration(),
			this.customerExpireAlertInMinutes * TimerUtils.MS_IN_MINUTE
		);
	}

	async notifyCustomerSessionExpiration() {
		if (!this.isAuth || this.customerSessionExpired) return;

		this.customerActivityNotificationId = await NotificationCore.addToastWithData({
			text: this.$t('login.sessionExpirationNotification') as string,
			status: 'important',
			remainingTime: this.customerExpireAlertShowInMinutes * TimerUtils.SECONDS_IN_MINUTE,
		}, this.customerExpireAlertShowInMinutes * TimerUtils.MS_IN_MINUTE);
	}

	trackCustomerActivity() {
		this.customerActivityTrackerId &&
			clearTimeout(this.customerActivityTrackerId);

		this.trackCustomerActivityNotification();

		this.customerActivityTrackerId = setTimeout(
			() => this.deactivateCustomer(),
			this.customerExpireInMinutes * TimerUtils.MS_IN_MINUTE
		);
	}

	deactivateCustomer() {
		if (!this.isAuth || this.customerSessionExpired) return;

		this.customerSessionExpiredDialog = true;
		authEntity.customerSessionExpiredState = true;

		auth.signOutLocal();
	}

	initCustomerActivationEventListeners() {
		const optimizedCustomerActivation = this.wrapActionWithAnimationFrameTick(
			() => this.activateCustomer()
		);

		this.customerActivationEventListeners.forEach(
			listener => listener(optimizedCustomerActivation)
		);
	}

	initCustomerActivity() {
		this.initCustomerActivationEventListeners();
		this.checkIsAuthAndTrackCustomerActivity();
	}

	@Watch('isAuth')
	isAuthChanged() {
		this.checkIsAuthAndTrackCustomerActivity();
	}

	@Watch('customerSessionExpired')
	customerSessionExpiredChanged() {
		this.activateCustomer();
	}

	@Watch('resetNotificationTrack')
	resetNotificationTrackChanged() {
		if (this.resetNotificationTrack) {

			this.customerActivityTrackerId && clearTimeout(this.customerActivityTrackerId);
			this.customerActivityNotifyTrackerId && clearTimeout(this.customerActivityNotifyTrackerId);

			this.setResetNotificationTrack(false);
		}
	}

}